<template>
  <div class="free-desks-tab">
    <div class="filters d-flex flex-wrap align-items-start py-3" style="gap: 0.75rem 1rem;">
      <b-form-group label="Floors" class="mb-0">
        <MultiSelectChips v-model="selectedFloors" :items="floorItems" placeholder="Search floors..." @search="floorSearch" />
      </b-form-group>
      <b-form-group label="From" class="mb-0">
        <div class="d-flex" style="gap: 0.25rem;">
          <AppDateSelect :date="from" @change="setDate('from', $event)" />
          <AppTimeSelect :time="from.format('HH:mm')" @change="setTime('from', $event)" style="width: 80px;" />
        </div>
      </b-form-group>
      <b-form-group label="To" class="mb-0">
        <div class="d-flex" style="gap: 0.25rem;">
          <AppDateSelect :date="to" :min-date="from" @change="setDate('to', $event)" />
          <AppTimeSelect :time="to.format('HH:mm')" :from-time="sameDay ? from.format('HH:mm') : ''" @change="setTime('to', $event)" style="width: 80px;" />
        </div>
      </b-form-group>
      <div class="ml-auto text-muted small pt-4">
        <span v-if="zonesAmbiguous">Pick one time zone to search</span>
        <span v-else-if="loading">Loading...</span>
        <span v-else>{{ pager.totalCount }} desk{{ pager.totalCount === 1 ? '' : 's' }} free for this window</span>
      </div>
      <div v-if="timezoneNote" class="zone-note w-100 text-muted small">
        <i class="ion ion-md-globe" /> {{ timezoneNote }}
      </div>
    </div>

    <b-card no-body>
      <div v-if="zonesAmbiguous" class="text-center m-4">
        <div class="text-big">{{ ambiguityNote }}</div>
        <b-btn v-if="narrowLabel" variant="outline-primary" class="mt-3" @click="narrowToNearestZone">
          {{ narrowLabel }}
        </b-btn>
      </div>
      <div class="table-responsive" v-else-if="desks.length">
        <b-table class="card-table" :items="desks" :fields="fields">
          <template #cell(desk)="data">
            {{ data.item.number || '—' }}
          </template>
          <template #cell(floor)="data">
            {{ data.item.floor_name }}
          </template>
          <template #cell(actions)="data">
            <b-btn variant="primary btn-sm" :to="bookLink(data.item)" target="_blank">
              <i class="ion ion-md-add" /> Book
            </b-btn>
          </template>
        </b-table>
      </div>
      <div v-else-if="loadFailed" class="text-center m-4 text-big text-muted">Could not load free desks.</div>
      <div v-else-if="!loading" class="text-center m-4 text-big">No free desks for this window on these floors.</div>

      <b-pagination
        v-if="pager.pageCount > 1"
        class="m-3"
        v-model="page"
        :total-rows="pager.totalCount"
        :per-page="pager.perPage"
      />
    </b-card>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapActions } from 'vuex'
import AppDateSelect from '@/components/shared/AppDateSelect'
import AppTimeSelect from '@/components/shared/AppTimeSelect'
import MultiSelectChips from '@/components/bookings/MultiSelectChips.vue'
import { isSameTimezone, SERVER_DATE_TIME_FORMAT, timezoneCity, toBuildingDate } from '@/components/desk/utils'
import {
  defaultFloorSelection,
  defaultSearchWindow,
  distinctTimezones,
  filtersFromQuery,
  filtersToQuery,
  floorOptions,
  inTimezone,
  nearestTimezone,
  searchTimezones
} from '@/components/bookings/utils'

const PER_PAGE = 20
const FILTER_DEBOUNCE_MS = 600
const MIN_WINDOW_MINUTES = 30

const emptyPager = () => ({ currentPage: 1, perPage: PER_PAGE, pageCount: 0, totalCount: 0 })

export default {
  name: 'FreeDesksTab',
  components: { AppDateSelect, AppTimeSelect, MultiSelectChips },
  data() {
    const allFloors = this.floorOptionsNow()
    // a link can name the floors to search; that is a choice, so it beats the default one
    const opened = filtersFromQuery(this.$route.query)
    const asked = allFloors.filter((floor) => opened.floors.includes(floor.id))
    const selectedFloors = asked.length ? asked : defaultFloorSelection(allFloors, dayjs.tz.guess())
    const zones = searchTimezones(selectedFloors, allFloors)
    const zone = zones.length === 1 ? zones[0] : dayjs.tz.guess()
    const askedWindow = opened.since && opened.until
    const { from, to } = askedWindow
      ? { from: opened.since.tz(zone), to: opened.until.tz(zone) }
      : defaultSearchWindow(dayjs().tz(zone))

    return {
      selectedFloors,
      // ids the link named that the store cannot name yet
      pendingFloorIds: asked.length || !opened.floors.length ? [] : opened.floors,
      floorTerm: '',
      floorItems: [],
      from,
      to,
      windowTouched: Boolean(askedWindow),
      desks: [],
      pager: emptyPager(),
      page: 1,
      loading: false,
      loadFailed: false,
      // a slower earlier response must not overwrite a newer one
      requestId: 0,
      reloadTimeoutId: null
    }
  },
  computed: {
    allFloors() {
      return this.floorOptionsNow()
    },
    browserTimezone() {
      return dayjs.tz.guess()
    },
    searchZones() {
      return searchTimezones(this.selectedFloors, this.allFloors)
    },
    zonesAmbiguous() {
      return this.searchZones.length > 1
    },
    searchTimezone() {
      return this.searchZones.length === 1 ? this.searchZones[0] : this.browserTimezone
    },
    timezoneNote() {
      if (this.searchZones.length !== 1 || isSameTimezone(this.searchTimezone, this.browserTimezone)) return ''
      return `Times are in ${timezoneCity(this.searchTimezone)} time - your local zone is different.`
    },
    ambiguityNote() {
      return this.zonesAmbiguous ? 'Select floors in a single time zone.' : ''
    },
    nearestZone() {
      return this.zonesAmbiguous ? nearestTimezone(this.searchZones, this.browserTimezone) : null
    },
    narrowLabel() {
      if (!this.nearestZone) return ''
      const verb = this.selectedFloors.length ? 'Keep only' : 'Search'
      return `${verb} ${timezoneCity(this.nearestZone)} floors`
    },
    showFloorZones() {
      return distinctTimezones(this.allFloors.map((floor) => floor.timezone)).length > 1
    },
    sameDay() {
      return this.from.format('YYYY-MM-DD') === this.to.format('YYYY-MM-DD')
    },
    fields() {
      return [
        { key: 'desk', label: 'Desk' },
        { key: 'floor', label: 'Floor' },
        { key: 'actions', label: '' }
      ]
    },
    windowState() {
      return { from: this.from, to: this.to, floors: this.selectedFloors }
    },
    /**
     * What the address should say about this tab.
     *
     * The office the screen picked by itself is left out, as is an untouched window: a reload
     * lands on both again anyway, and a link carrying today's default window would open
     * tomorrow on a search that is already over.
     */
    addressQuery() {
      const picked = this.selectedFloors.map((floor) => floor.id)
      const preset = defaultFloorSelection(this.allFloors, this.browserTimezone).map((floor) => floor.id)
      const isPreset = picked.length === preset.length && picked.every((id) => preset.includes(id))

      return filtersToQuery({
        floors: this.pendingFloorIds.length ? this.pendingFloorIds : isPreset ? [] : picked,
        since: this.windowTouched ? this.from : null,
        until: this.windowTouched ? this.to : null
      })
    }
  },
  methods: {
    ...mapActions('desk', ['desksFreeSearch']),
    ...mapActions('floor', ['floorsLoad']),
    // data() runs before computeds exist, so it can't use allFloors
    floorOptionsNow() {
      const getBuilding = this.$store.getters['building/getBuilding']
      return floorOptions(this.$store.getters['floor/floors'] || [], (floor) => {
        const building = getBuilding ? getBuilding(floor.building_id) : null
        return building ? building.timezone : null
      })
    },
    async load() {
      const requestId = ++this.requestId

      if (this.zonesAmbiguous) {
        this.desks = []
        this.pager = emptyPager()
        this.loadFailed = false
        this.loading = false
        return
      }

      this.loading = true
      try {
        const filters = {
          since: this.from.format(SERVER_DATE_TIME_FORMAT),
          until: this.to.format(SERVER_DATE_TIME_FORMAT)
        }
        if (this.selectedFloors.length) filters.floor = this.selectedFloors.map((floor) => floor.id)

        const response = await this.desksFreeSearch({ params: { filters, page: this.page, 'per-page': PER_PAGE } })
        if (requestId !== this.requestId) return
        this.desks = response.data.items
        this.pager = response.data.pager
        this.loadFailed = false
      } catch (e) {
        if (requestId !== this.requestId) return
        this.desks = []
        this.pager = emptyPager()
        this.loadFailed = true
      } finally {
        if (requestId === this.requestId) this.loading = false
      }
    },
    reloadSoon() {
      clearTimeout(this.reloadTimeoutId)
      this.reloadTimeoutId = setTimeout(() => {
        this.reloadTimeoutId = null
        this.load()
      }, FILTER_DEBOUNCE_MS)
    },
    setDate(field, date) {
      const day = toBuildingDate(date, this.searchTimezone)
      this.setBound(field, day.hour(this[field].hour()).minute(this[field].minute()))
    },
    setTime(field, time) {
      this.setBound(field, this[field].hour(time.hour).minute(time.minute))
    },
    setBound(field, value) {
      // pickers echo their value back as @change on mount
      if (value.isSame(this[field])) return

      this.windowTouched = true
      const minutes = Math.max(this.to.diff(this.from, 'minute'), MIN_WINDOW_MINUTES)
      this[field] = value
      // moving From past To shifts To, keeping the window length
      if (field === 'from' && !this.to.isAfter(this.from)) {
        this.to = this.from.add(minutes, 'minute')
      }
    },
    narrowToNearestZone() {
      const scope = this.selectedFloors.length ? this.selectedFloors : this.allFloors
      this.selectedFloors = scope.filter((floor) => isSameTimezone(floor.timezone, this.nearestZone))
    },
    bookLink(desk) {
      return {
        name: 'map',
        params: { floorId: desk.floor_id },
        query: {
          pointId: desk.point_id,
          bookFrom: this.from.format(SERVER_DATE_TIME_FORMAT),
          bookTo: this.to.format(SERVER_DATE_TIME_FORMAT)
        }
      }
    },
    floorSearch(term) {
      this.floorTerm = term
      this.applyFloorTerm()
    },
    applyFloorTerm() {
      const needle = this.floorTerm.toLowerCase()
      this.floorItems = this.allFloors
        .filter((floor) => !needle || floor.label.toLowerCase().includes(needle))
        .map((floor) =>
          this.showFloorZones && floor.timezone ? { ...floor, hint: timezoneCity(floor.timezone) } : floor)
    }
  },
  watch: {
    addressQuery: {
      immediate: true,
      handler(query) {
        this.$emit('query-change', query)
      }
    },
    allFloors(floors, previous) {
      this.applyFloorTerm()

      if (this.pendingFloorIds.length) {
        const picked = floors.filter((floor) => this.pendingFloorIds.includes(floor.id))
        this.pendingFloorIds = []
        if (picked.length) {
          this.selectedFloors = picked
          return
        }
      }

      // floors arrived after mount: apply the default selection once
      if (!previous.length && !this.selectedFloors.length) {
        this.selectedFloors = defaultFloorSelection(floors, this.browserTimezone)
      }
    },
    searchTimezone(zone, previous) {
      if (!previous || isSameTimezone(zone, previous)) return

      if (this.windowTouched) {
        this.from = inTimezone(this.from, zone)
        this.to = inTimezone(this.to, zone)
      } else {
        const { from, to } = defaultSearchWindow(dayjs().tz(zone))
        this.from = from
        this.to = to
      }
    },
    windowState: {
      deep: true,
      handler() {
        this.page = 1
        this.reloadSoon()
      }
    },
    page() {
      // a filter change already scheduled a reload
      if (this.reloadTimeoutId) return
      this.load()
    }
  },
  created() {
    this.floorsLoad()
    this.applyFloorTerm()
    this.load()
  },
  beforeDestroy() {
    clearTimeout(this.reloadTimeoutId)
  }
}
</script>

<style scoped lang="scss">
.filters {
  border-bottom: 1px solid #eee;
}
</style>
